import { useState } from 'react';
import { useRouter } from 'next/router';
import { differenceInDays } from 'date-fns';
import api from 'api';
import {
  getDefaultEmailTemplate,
  renderPlaceholdersInTemplates
} from 'shared-helpers/email';
import { useDispatch, useReduxState } from 'hooks';
import { breakpoints } from 'styles';
import { useApiData } from 'lib/api';
import { getFormattedDate } from 'lib/common';
import {
  DisplayFormattedDate,
  EmptyIndicator,
  FlexContainer,
  MoreOptions,
  Pagination,
  ProductLinkWithIcon,
  Tooltip,
  User
} from 'components/common';
import {
  Box,
  BoxTable,
  BoxTableCell,
  BoxTableHeader,
  BoxTableRow
} from 'components/common/box';
import { StatCount } from 'components/common/stats';
import {
  MyTeamReviewCompletionsMarkReviewed,
  MyTeamReviewCompletionsMarkReviewedAll,
  MyTeamReviewFilters,
  MyTeamReviewMarkAsHidden,
  MyTeamReviewSendReminder
} from '.';

import type { EmailTemplateData } from 'shared-types';

type EmailTemplatesByCompletionIdentifier = {
  [identifier: string]: {
    emailTemplates: EmailTemplateData[];
    emailTemplatesDefault: EmailTemplateData;
  };
};

interface Props {
  completionStatus: 'expiring' | 'expired';
}

const MyTeamReviewCompletions = ({ completionStatus }: Props) => {
  const {
    language,
    messages,
    serverConfig: { adminUrl },
    theme,
    user
  } = useReduxState();

  const { dispatch } = useDispatch();

  const router = useRouter();

  const filters = Object.keys(router.query || {})
    .map((key) => `&${key}=${router.query[key]}`)
    .join('');

  const [
    emailTemplatesByCompletionIdentifier,
    setEmailTemplatesByCompletionIdentifier
  ] = useState<EmailTemplatesByCompletionIdentifier>({});

  const achievementEmailType =
    completionStatus === 'expiring'
      ? 'achievement-expiring'
      : 'achievement-expired';

  const courseEmailType =
    completionStatus === 'expiring'
      ? 'completion-expiring'
      : 'completion-expired';

  const updateEmailTemplates = async (updatedData) => {
    try {
      const templatesByIdentifier: EmailTemplatesByCompletionIdentifier = {};

      await Promise.all(
        updatedData.completions.map(async (completion) => {
          const response = await api.get(
            completion.type === 'achievement'
              ? `/admin/email-templates-data?type=${achievementEmailType}&achievementId=${completion.achievement.id}`
              : `/admin/email-templates-data?type=${courseEmailType}&courseId=${completion.course.id}`
          );

          let emailTemplates = response.data?.emailTemplates || [];

          let placeholderValues: { [key: string]: string } = {};

          if (completion.type === 'achievement') {
            placeholderValues = {
              achievementTitle: completion.achievement.title,
              achievementUrl: `${adminUrl}/achievements/${completion.achievement.id}`,
              contactEmail: user?.email,
              ...(achievementEmailType === 'achievement-expiring'
                ? {
                    expirationDate: getFormattedDate({ date: completion.date }),
                    expirationTimeInDays: `${differenceInDays(
                      Date.parse(completion.date),
                      Date.now()
                    )}`
                  }
                : {})
            };
          }

          if (completion.type === 'course') {
            placeholderValues = {
              contactEmail: user?.email,
              courseTitle: completion.course.title,
              courseUrl: `${adminUrl}/courses/${completion.course.slug}`,
              ...(courseEmailType === 'completion-expiring'
                ? {
                    expirationDate: getFormattedDate({ date: completion.date }),
                    expirationTimeInDays: `${differenceInDays(
                      Date.parse(completion.date),
                      Date.now()
                    )}`
                  }
                : {})
            };
          }

          emailTemplates = renderPlaceholdersInTemplates({
            emailTemplates,
            overridePlaceholderValues: placeholderValues
          });

          const emailTemplatesDefault = getDefaultEmailTemplate({
            language,
            emailTemplates
          });

          templatesByIdentifier[completion.identifier] = {
            emailTemplates,
            emailTemplatesDefault
          };
        })
      );

      setEmailTemplatesByCompletionIdentifier(templatesByIdentifier);
    } catch (error) {
      await dispatch.handleErrorDispatch(error);
    }
  };

  const { data, loading, updateData } = useApiData({
    onDataUpdated: (data) => updateEmailTemplates(data),
    url:
      `/admin/supervisors/${user?.id}/supervisees/${completionStatus}` +
      (filters?.length ? `?${filters}` : '')
  });

  const title =
    completionStatus === 'expiring'
      ? messages.myTeamReviewExpiring
      : messages.myTeamReviewExpired;

  return (
    <>
      <Box
        headerContent={
          <FlexContainer style={{ alignItems: 'center', gap: 10 }}>
            <MoreOptions category="lighter" styleContentButtons={false}>
              <MyTeamReviewCompletionsMarkReviewedAll
                type={completionStatus}
                unreviewedCompletions={data?.unreviewedCompletions || 0}
                updateCompletions={updateData}
              />
            </MoreOptions>
          </FlexContainer>
        }
        headerContentWrap={true}
        subHeaderContent={
          <MyTeamReviewFilters
            defaultSort={messages.commonExpirationDate}
            includeFilters={true}
          />
        }
        loading={loading}
        padding={0}
        title={
          <div style={{ display: 'flex', fontWeight: 600, gap: 5 }}>
            <StatCount
              isLarge={true}
              label={title}
              labelStyle={{ color: theme.textColorLight, fontWeight: 700 }}
              value={data?.numberOfCompletions || 0}
            />
            {data?.unreviewedCompletions ? (
              <StatCount
                category="error-light"
                isLarge={true}
                label={messages.myTeamReviewUnreviewed}
                labelStyle={{ color: theme.textColorLight }}
                style={{ marginLeft: 20 }}
                value={data?.unreviewedCompletions}
              />
            ) : null}
            {data?.hiddenCompletions ? (
              <StatCount
                isLarge={true}
                label={messages.myTeamReviewHidden}
                labelStyle={{ color: theme.textColorLight }}
                style={{ marginLeft: 20 }}
                value={data?.hiddenCompletions}
              />
            ) : null}
          </div>
        }
      >
        {data?.completions?.length ? (
          <BoxTable>
            <BoxTableHeader hideAtBreakpoint={breakpoints.medium}>
              <BoxTableCell
                cellBreakpoint={breakpoints.medium}
                shrink={true}
                style={{ padding: '10px 0 10px 15px' }}
              />
              <BoxTableCell cellBreakpoint={breakpoints.medium}>
                {messages.commonUser}
              </BoxTableCell>
              <BoxTableCell cellBreakpoint={breakpoints.medium}>
                {messages.commonTitle}
              </BoxTableCell>
              <BoxTableCell cellBreakpoint={breakpoints.medium}>
                {messages.commonExpirationDate}
              </BoxTableCell>
              <BoxTableCell cellBreakpoint={breakpoints.medium} shrink={true} />
            </BoxTableHeader>

            {data.completions.map((completion, i: number) => (
              <BoxTableRow key={i}>
                <BoxTableCell
                  verticalBreakpoint={breakpoints.medium}
                  shrink={true}
                  style={{ padding: '10px 0 10px 15px' }}
                >
                  {completion.isRead ? null : (
                    <Tooltip
                      childrenWrapInDiv={false}
                      content={messages.myTeamReviewUnreviewed}
                    >
                      <div style={{ margin: -5, padding: 5 }}>
                        <div
                          style={{
                            backgroundColor: theme.dangerColor,
                            width: 10,
                            height: 10,
                            borderRadius: '100%'
                          }}
                        />
                      </div>
                    </Tooltip>
                  )}
                </BoxTableCell>
                <BoxTableCell cellBreakpoint={breakpoints.medium}>
                  <User enableProfileLink={true} user={completion.user} />
                </BoxTableCell>
                <BoxTableCell cellBreakpoint={breakpoints.medium}>
                  <ProductLinkWithIcon
                    accessType="supervisor"
                    achievement={completion.achievement}
                    course={completion.course}
                    userId={completion.user.id}
                  />
                </BoxTableCell>
                <BoxTableCell
                  cellBreakpoint={breakpoints.medium}
                  icon={['far', 'calendar-alt']}
                  label={messages.commonExpirationDate}
                >
                  {completion.date ? (
                    <DisplayFormattedDate date={completion.date} />
                  ) : null}
                </BoxTableCell>
                <BoxTableCell
                  shrink={true}
                  valueContainerStyle={{ justifyContent: 'flex-end' }}
                  verticalBreakpoint={breakpoints.medium}
                >
                  <FlexContainer style={{ alignItems: 'center', gap: 10 }}>
                    <MyTeamReviewCompletionsMarkReviewed
                      id={completion.id}
                      isRead={completion.isRead}
                      updateCompletions={updateData}
                    />

                    <MoreOptions category="lighter">
                      <MyTeamReviewSendReminder
                        automatedReminders={completion.automatedReminders}
                        buttonIsDropdown={true}
                        emailLogType={
                          completion.course
                            ? completionStatus === 'expiring'
                              ? 'completion-expiring'
                              : 'completion-expired'
                            : completionStatus === 'expiring'
                            ? 'achievement-expiring'
                            : 'achievement-expired'
                        }
                        emailTemplates={
                          emailTemplatesByCompletionIdentifier[
                            completion.identifier
                          ]?.emailTemplates
                        }
                        emailTemplatesDefault={
                          emailTemplatesByCompletionIdentifier[
                            completion.identifier
                          ]?.emailTemplatesDefault
                        }
                        isCompletion={true}
                        targetUser={completion.user}
                      />

                      <MyTeamReviewMarkAsHidden
                        id={completion.id}
                        isHidden={completion.isHidden}
                        isRead={completion.isRead}
                        updateCompletions={updateData}
                      />
                    </MoreOptions>
                  </FlexContainer>
                </BoxTableCell>
              </BoxTableRow>
            ))}
          </BoxTable>
        ) : (
          <EmptyIndicator
            icon={['far', 'calendar-alt']}
            title={messages.myTeamReviewNoCompletions}
          />
        )}
      </Box>

      <Pagination
        currentPage={Number(router.query.page) || 1}
        numberOfPages={data?.pages || 1}
        onPageChange={({ page }) => {
          router.replace({ query: { ...router.query, page } }, undefined, {
            shallow: true
          });
        }}
      />
    </>
  );
};

export default MyTeamReviewCompletions;
